import { useMemo } from 'react';
import { getLocale, useIntl } from 'umi';
import { dynamicString } from '@home/utils/dynamicString';
import { renderCountryCell, renderValueCell, renderShareCell } from '@/pages/dashboard/components/CountryRegionRowItem/CountryTabBreakdown/renderUtils';
import { useSelectorMap } from '@/pages/dashboard/utils/customHook';
import { formatRuleToTypeMap } from '@/pages/dashboard/pages/overview/components/RankListCo/_costant';

/** 从 formatRule 推断 tooltipsType，与 RankListCo useMetricsConfig 保持一致 */
function inferTooltipsTypeFromFormatRule(formatRule: string): 'int' | 'decimals' | 'percent' {
  const lower = formatRule?.toLowerCase() || '';
  if (formatRuleToTypeMap?.[formatRule]) {
    return formatRuleToTypeMap[formatRule];
  }
  if (lower.includes('percent')) return 'percent';
  if (lower.includes('decimal')) return 'decimals';
  if (lower.includes('int') || lower.includes('thousands') || lower.includes('bmk')) return 'int';
  return 'decimals';
}

export interface IUseCommonRankMetricsConfigParams {
  componentMetrics: any;
  comConfig: any;
  accumated: string;
  data: any;
  title: string;
  rangeTitle: string;
  isSingle: boolean;
  tabMsg?: any;
  timeType?: string;
  chartTooltipsName?: string;
}

export interface IUseCommonRankMetricsConfigReturn {
  tableColumns: any[] | undefined;
  chartTooltipsConfig: { tooltipsName?: string; tooltipsType?: string; tooltipsDecimal?: number; tooltipsPercentDecimals?: number; } | undefined;
  hasFormatRuleConfig: boolean;
}

/**
 * 处理 CommonRankListCo 的指标 / 小数位 / share 配置（formatRule 等）
 * 参照 RankListCo useMetricsConfig：有配置时返回带自定义 render 的 tableColumns 与推断的 chartTooltipsConfig，无配置时返回 undefined 走原有逻辑
 */
export const useCommonRankMetricsConfig = (
  params: IUseCommonRankMetricsConfigParams
): IUseCommonRankMetricsConfigReturn => {
  const {
    componentMetrics,
    comConfig,
    accumated,
    data,
    title,
    rangeTitle,
    isSingle,
    tabMsg,
    timeType,
    chartTooltipsName,
  } = params;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isEn = locale === 'en-US';
  const { getFilterItemText } = useSelectorMap();

  const hasFormatRuleConfig = Boolean(
    componentMetrics?.formatRule ||
      componentMetrics?.changeFormatRule ||
      componentMetrics?.shareFormatRule
  );

  const resolvedFormatRule = useMemo(() => {
    if (!hasFormatRuleConfig) return '';
    return dynamicString(componentMetrics?.formatRule, data) || '';
  }, [hasFormatRuleConfig, componentMetrics?.formatRule, data]);

  const resolvedChangeFormatRule = useMemo(() => {
    if (!hasFormatRuleConfig) return '';
    return dynamicString(componentMetrics?.changeFormatRule, data) || '';
  }, [hasFormatRuleConfig, componentMetrics?.changeFormatRule, data]);

  const resolvedShareFormatRule = useMemo(() => {
    if (!hasFormatRuleConfig) return '';
    return dynamicString(componentMetrics?.shareFormatRule, data) || '';
  }, [hasFormatRuleConfig, componentMetrics?.shareFormatRule, data]);

  const tableColumns = useMemo((): any[] | undefined => {
    if (!hasFormatRuleConfig) {
      return undefined;
    }
    const hasValueFormatRule = Boolean(resolvedFormatRule);
    const hasShareFormatRule = Boolean(resolvedShareFormatRule);

    const titleColumnsObj: any = {
      country: formatMessage({ id: 'common_Country' }),
      region: formatMessage({ id: 'common_Region' }),
      channel: formatMessage({ id: 'common_Channel' }),
      zone: formatMessage({ id: 'common_Zone' }),
      os: formatMessage({ id: 'common_System' }),
      lang: formatMessage({ id: 'common_Language' }),
      bundle: formatMessage({ id: 'common_Bundle' }),
    };
    if (tabMsg?.[accumated]) {
      const tabConfig = tabMsg[accumated];
      const aliasName = isEn
        ? (tabConfig.aliasNameEn || tabConfig.nameEn)
        : (tabConfig.aliasNameCn || tabConfig.nameCn);
      if (aliasName) {
        titleColumnsObj[accumated] = aliasName;
      }
    }

    const isUserQuota = componentMetrics?.type === 'int';
    const valueColumn = hasValueFormatRule
      ? {
        key: componentMetrics?.reqMetricKey,
        header: isSingle ? title : rangeTitle,
        align: 'right' as const,
        dataType: 'dau',
        valueName: componentMetrics?.metricKey || 'value',
        ratioField: componentMetrics?.ratioField || 'change',
        render: (record: any) =>
          renderValueCell({
            record,
            formatRule: resolvedFormatRule,
            changeFormatRule: resolvedChangeFormatRule || undefined,
            isUserQuota,
          }),
      }
      : {
        key: componentMetrics?.reqMetricKey,
        header: isSingle ? title : rangeTitle,
        align: 'right' as const,
        dataType: 'integer',
        decimalObj: {
          type: componentMetrics?.type,
          decimal: componentMetrics?.decimal,
          percentDecimals: componentMetrics?.percentDecimals,
        },
        ratioField: componentMetrics?.ratioField || 'change',
        valueName: componentMetrics?.metricKey || 'value',
      };

    const result: any[] = [
      {
        key: 'name',
        header: dynamicString(titleColumnsObj?.[accumated], data),
        align: 'left',
        dataType: accumated,
        render: (record: any) =>
          renderCountryCell({ record, regionDataType: accumated, getFilterItemText }),
      },
      valueColumn,
    ];

    if (componentMetrics?.extraComponentMetrics?.length) {
      result.push(
        ...componentMetrics.extraComponentMetrics.map((extra: any) => {
          const titleResultObj: any = {
            day: extra?.[isEn ? 'enName' : 'cnName'],
            week: extra?.[isEn ? 'enWeekName' : 'cnWeekName'],
            month: extra?.[isEn ? 'enMonthName' : 'cnMonthName'],
          };
          const rangeTitleObj: any = {
            day: extra?.[isEn ? 'rangeEnName' : 'rangeCnName'],
            week: extra?.[isEn ? 'rangeEnWeekName' : 'rangeCnWeekName'],
            month: extra?.[isEn ? 'rangeEnMonthName' : 'rangeCnMonthName'],
          };
          const extraTableColumnTitle =
            dynamicString(titleResultObj?.[timeType?.toLowerCase() || 'day'], data) || titleResultObj?.day;
          const extraRangeTableColumnTitle = dynamicString(
            rangeTitleObj?.[timeType?.toLowerCase() || 'day'],
            data
          );
          return {
            key: extra?.reqMetricKey,
            header: isSingle ? extraTableColumnTitle : extraRangeTableColumnTitle,
            align: 'right',
            dataType: extra?.dataType || 'integer',
            decimalObj: {
              type: extra?.type,
              decimal: extra?.decimal,
              percentDecimals: extra?.percentDecimals,
            },
            ratioField: extra?.ratioFiled || '',
            valueName: extra?.metricKey,
          };
        })
      );
    }

    const shareKey = isEn ? componentMetrics?.shareKey || '' : componentMetrics?.shareKeyCn || '';
    const shareHeader = shareKey || formatMessage({ id: 'overview_Share_Of_All' });

    if (hasShareFormatRule && comConfig?.isHaveShare !== false) {
      result.push({
        key: 'share_of_all',
        header: shareHeader,
        align: 'right',
        dataType: 'oneDecimalPercent',
        render: (record: any) =>
          renderShareCell({ record, shareFormatRule: resolvedShareFormatRule }),
      });
    } else {
      result.push({
        key: 'share_of_all',
        header: shareHeader,
        align: 'right',
        dataType: 'oneDecimalPercent',
      });
    }

    return result;
  }, [
    hasFormatRuleConfig,
    resolvedFormatRule,
    resolvedChangeFormatRule,
    resolvedShareFormatRule,
    componentMetrics,
    comConfig?.isHaveShare,
    accumated,
    data,
    title,
    rangeTitle,
    isSingle,
    tabMsg,
    isEn,
    timeType,
    formatMessage,
    getFilterItemText,
  ]);

  const chartTooltipsConfig = useMemo((): IUseCommonRankMetricsConfigReturn['chartTooltipsConfig'] => {
    if (!hasFormatRuleConfig || !resolvedFormatRule) {
      return undefined;
    }
    const tooltipsType = inferTooltipsTypeFromFormatRule(resolvedFormatRule);
    let tooltipsDecimal = 2;
    if (tooltipsType === 'int') {
      tooltipsDecimal = 0;
    }
    return {
      tooltipsName: chartTooltipsName,
      tooltipsType,
      tooltipsDecimal,
    };
  }, [hasFormatRuleConfig, resolvedFormatRule, chartTooltipsName]);

  return {
    tableColumns: hasFormatRuleConfig ? tableColumns : undefined,
    chartTooltipsConfig: hasFormatRuleConfig ? chartTooltipsConfig : undefined,
    hasFormatRuleConfig,
  };
};
